// Where error reports come from: the page's uncaught errors and unhandled rejections (in production too: the harness's
// capture is dev only), and the kernel's fail-soft log (a system that threw and was switched off, decision 0033),
// read from its status list so the kernel needs no telemetry hook. Each source reports one error once.
import type { ErrorInput } from "./telemetry.js";

/** An event target with `addEventListener` (window). */
export interface ErrorTarget {
  addEventListener(type: string, fn: (e: never) => void): void;
  removeEventListener(type: string, fn: (e: never) => void): void;
}

/** Report the target's uncaught errors and unhandled rejections; returns the stop function. */
export function listenForErrors(target: ErrorTarget, report: (e: ErrorInput) => void): () => void {
  const onError = (e: { error?: { stack?: string; message?: string }; message?: string }): void =>
    report({ source: "uncaught", message: e.error?.message ?? String(e.message ?? "error"), ...(e.error?.stack ? { stack: e.error.stack } : {}) });
  const onRejection = (e: { reason?: unknown }): void => {
    const r = e.reason;
    report({ source: "rejection", message: r instanceof Error ? r.message : String(r), ...(r instanceof Error && r.stack ? { stack: r.stack } : {}) });
  };
  target.addEventListener("error", onError as (e: never) => void);
  target.addEventListener("unhandledrejection", onRejection as (e: never) => void);
  return () => {
    target.removeEventListener("error", onError as (e: never) => void);
    target.removeEventListener("unhandledrejection", onRejection as (e: never) => void);
  };
}

/** A system's health as a kernel lists it (`Game.systemStatus()`). */
export interface SystemHealth {
  name: string;
  module: string;
  errors: number;
  disabled: boolean;
  firstError?: string;
}

/** Watch a kernel's system list: `check()` reports each system newly switched off by the fail-soft policy, once. */
export function failSoftWatcher(status: () => readonly SystemHealth[], report: (e: ErrorInput) => void): { check(): number } {
  const seen = new Set<string>();
  return {
    check() {
      let n = 0;
      for (const s of status()) {
        if (!s.disabled || s.errors === 0 || seen.has(s.name)) continue;
        seen.add(s.name);
        n++;
        report({ source: "system", message: s.firstError ?? "system switched off", detail: { system: s.name, module: s.module, errors: s.errors } });
      }
      return n;
    },
  };
}

/** Coarse device facts from a navigator: OS and browser family, cores and memory class. No user-agent string, no
 *  identifiers (decision 0064: anonymous). */
export function coarseDevice(nav: { userAgent?: string; hardwareConcurrency?: number; deviceMemory?: number; maxTouchPoints?: number } | undefined): { os: string; browser: string; cores: number | null; memoryGb: number | null; touch: boolean } {
  const ua = nav?.userAgent ?? "";
  const os = /Android/i.test(ua) ? "android" : /iPhone|iPad|iPod/i.test(ua) ? "ios" : /Windows/i.test(ua) ? "windows" : /Mac OS X|Macintosh/i.test(ua) ? "mac" : /Linux|CrOS/i.test(ua) ? "linux" : "other";
  const browser = /Edg\//.test(ua) ? "edge" : /Firefox\//.test(ua) ? "firefox" : /Chrome\/|CriOS\//.test(ua) ? "chrome" : /Safari\//.test(ua) ? "safari" : "other";
  return { os, browser, cores: nav?.hardwareConcurrency ?? null, memoryGb: nav?.deviceMemory ?? null, touch: (nav?.maxTouchPoints ?? 0) > 0 };
}
